import React, { useRef, useEffect, useCallback } from 'react';

const FruitNinjaGame = ({ onGameOver, opponentScore, onAction, isMyTurn = true }) => {
  const canvasRef = useRef(null);
  const [isMobile, setIsMobile] = React.useState(false);
  const gameState = useRef({
    score: 0,
    combo: 0,
    timeLeft: 60,
    gameActive: true,
    fruits: [],
    particles: [],
    trail: [],
    isDrawing: false,
    comboTimeout: null,
    lastSecond: 0,
  }).current;

  // 移动端检测
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getFruitTypes = () => [
    { name: 'apple', color1: '#e74c3c', color2: '#c0392b', points: 10, radius: isMobile ? 28 : 22 },
    { name: 'banana', color1: '#f1c40f', color2: '#f39c12', points: 10, radius: isMobile ? 26 : 20 },
    { name: 'orange', color1: '#e67e22', color2: '#d35400', points: 10, radius: isMobile ? 27 : 21 },
    { name: 'watermelon', color1: '#2ecc71', color2: '#27ae60', points: 20, radius: isMobile ? 34 : 28 },
    { name: 'bomb', color1: '#2c3e50', color2: '#1a252f', points: -50, radius: isMobile ? 30 : 24, isBomb: true }
  ];

  class Fruit {
    constructor(x, y, type, speedMultiplier = 1.0) {
      this.x = x;
      this.y = y;
      this.type = type;
      let baseVx = (Math.random() - 0.5) * 300;
      let baseVy = -Math.random() * 400 - 200;
      this.vx = baseVx * speedMultiplier;
      this.vy = baseVy * speedMultiplier;
      this.gravity = 800;
      this.radius = type.radius;
      this.points = type.points;
      this.isBomb = type.isBomb || false;
      this.angle = Math.random() * Math.PI * 2;
      this.angularVel = (Math.random() - 0.5) * 4;
    }
    update(deltaSec, canvasHeight) {
      this.x += this.vx * deltaSec;
      this.y += this.vy * deltaSec;
      this.vy += this.gravity * deltaSec;
      this.angle += this.angularVel * deltaSec;
      return this.y > canvasHeight + 100 || this.x < -100 || this.x > (canvasRef.current?.width || 800) + 100;
    }
    draw(ctx) {
      ctx.save();
      ctx.shadowBlur = 8;
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.translate(this.x, this.y);
      ctx.rotate(this.angle);
      ctx.beginPath();
      ctx.ellipse(0, 0, this.radius, this.radius * 0.9, 0, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(-5, -5, 5, 0, 0, this.radius);
      gradient.addColorStop(0, this.type.color1);
      gradient.addColorStop(1, this.type.color2);
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(-5, -5, 5, 4, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fill();
      if (this.isBomb) {
        ctx.font = `bold ${this.radius}px sans-serif`;
        ctx.fillStyle = '#000';
        ctx.shadowBlur = 0;
        ctx.fillText('💣', -12, 10);
      } else {
        ctx.beginPath();
        ctx.moveTo(this.radius * 0.6, -this.radius * 0.8);
        ctx.lineTo(this.radius * 0.9, -this.radius * 1.2);
        ctx.lineTo(this.radius * 0.3, -this.radius * 1.0);
        ctx.fillStyle = '#2ecc71';
        ctx.fill();
      }
      ctx.restore();
    }
  }

  class Particle {
    constructor(x, y, color) {
      this.x = x;
      this.y = y;
      this.vx = (Math.random() - 0.5) * 200;
      this.vy = (Math.random() - 0.5) * 200 - 100;
      this.life = 1;
      this.color = color;
    }
    update(deltaSec) {
      this.x += this.vx * deltaSec;
      this.y += this.vy * deltaSec;
      this.vy += 400 * deltaSec;
      this.life -= 2 * deltaSec;
      return this.life <= 0;
    }
    draw(ctx) {
      ctx.save();
      ctx.globalAlpha = this.life;
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  const addParticles = (x, y, color) => {
    for (let i = 0; i < 12; i++) {
      gameState.particles.push(new Particle(x, y, color));
    }
  };

  const checkSlice = useCallback((x, y) => {
    const sliceRadius = isMobile ? 20 : 12;
    for (let i = 0; i < gameState.fruits.length; i++) {
      const fruit = gameState.fruits[i];
      const dx = fruit.x - x;
      const dy = fruit.y - y;
      const dist = Math.sqrt(dx*dx + dy*dy);
      if (dist < fruit.radius + sliceRadius) {
        gameState.fruits.splice(i, 1);
        addParticles(fruit.x, fruit.y, fruit.type.color1);
        if (fruit.isBomb) {
          gameState.score = Math.max(0, gameState.score + fruit.points);
          gameState.combo = 0;
          if (gameState.comboTimeout) clearTimeout(gameState.comboTimeout);
          setTimeout(() => onAction && onAction('bomb', fruit.points), 0);
        } else {
          const addScore = fruit.points * (1 + Math.floor(gameState.combo / 10));
          gameState.score += addScore;
          gameState.combo += 1;
          if (gameState.comboTimeout) clearTimeout(gameState.comboTimeout);
          gameState.comboTimeout = setTimeout(() => { gameState.combo = 0; }, 2000);
          setTimeout(() => onAction && onAction('slice', addScore, gameState.combo - 1), 0);
        }
        return;
      }
    }
  }, [onAction, isMobile, gameState]);

  const spawnFruit = useCallback(() => {
    if (!gameState.gameActive) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const width = canvas.width;
    const randomX = 60 + Math.random() * (width - 120);
    const fruitTypes = getFruitTypes();
    let type;
    if (Math.random() < 0.12) {
      type = fruitTypes.find(f => f.isBomb);
    } else {
      const normalFruits = fruitTypes.filter(f => !f.isBomb);
      type = normalFruits[Math.floor(Math.random() * normalFruits.length)];
    }
    const isFast = !type.isBomb && Math.random() < 0.25;
    const speedMultiplier = isFast ? 1.6 : 1.0;
    const fruit = new Fruit(randomX, canvas.height - 50, type, speedMultiplier);
    gameState.fruits.push(fruit);
  }, [gameState]);

  // 倒计时独立定时器（每200ms检查一次，轻量级）
  useEffect(() => {
    if (!gameState.gameActive) return;
    const timer = setInterval(() => {
      if (!gameState.gameActive) return;
      const now = Date.now() / 1000;
      if (gameState.lastSecond === 0) gameState.lastSecond = now;
      const elapsed = now - gameState.lastSecond;
      if (elapsed >= 1.0) {
        const decrement = Math.floor(elapsed);
        gameState.timeLeft = Math.max(0, gameState.timeLeft - decrement);
        gameState.lastSecond = now;
        if (gameState.timeLeft <= 0) {
          gameState.gameActive = false;
          onGameOver(gameState.score);
          clearInterval(timer);
        }
      }
    }, 200);
    return () => clearInterval(timer);
  }, [gameState, onGameOver]);

  // 动画循环（仅负责渲染，不处理倒计时逻辑）
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // 物理更新时间差（仅用于水果移动）
    const now = performance.now();
    let delta = 0.016;
    if (gameState.lastTimestamp !== 0) {
      delta = Math.min(0.033, (now - gameState.lastTimestamp) / 1000);
    }
    gameState.lastTimestamp = now;
    
    // 清屏背景
    ctx.clearRect(0, 0, width, height);
    const grad = ctx.createLinearGradient(0, 0, 0, height);
    grad.addColorStop(0, '#1a2a3a');
    grad.addColorStop(1, '#0a0f1e');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
    
    // 轨迹
    if (gameState.trail.length > 1) {
      ctx.beginPath();
      ctx.moveTo(gameState.trail[0].x, gameState.trail[0].y);
      for (let i = 1; i < gameState.trail.length; i++) {
        ctx.lineTo(gameState.trail[i].x, gameState.trail[i].y);
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 8;
      ctx.stroke();
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#f39c12';
      ctx.stroke();
    }
    
    // 水果
    for (let i = 0; i < gameState.fruits.length; i++) {
      const removed = gameState.fruits[i].update(delta, height);
      if (removed) {
        gameState.fruits.splice(i, 1);
        i--;
      } else {
        gameState.fruits[i].draw(ctx);
      }
    }
    
    // 粒子
    for (let i = 0; i < gameState.particles.length; i++) {
      const dead = gameState.particles[i].update(delta);
      if (dead) {
        gameState.particles.splice(i, 1);
        i--;
      } else {
        gameState.particles[i].draw(ctx);
      }
    }
    
    // UI
    ctx.font = 'bold 28px "Segoe UI", system-ui';
    ctx.fillStyle = '#fff';
    ctx.fillText(`🍎 ${Math.floor(gameState.score)}`, 20, 60);
    if (gameState.combo > 3) {
      ctx.font = 'bold 24px system-ui';
      ctx.fillStyle = '#f39c12';
      ctx.fillText(`${gameState.combo} 连击！`, 20, 110);
    }
    ctx.font = '20px system-ui';
    ctx.fillStyle = '#aaa';
    ctx.fillText(`对手: ${opponentScore}`, 20, 160);
    ctx.font = '28px monospace';
    ctx.fillStyle = '#fff';
    ctx.fillText(`⏱️ ${Math.ceil(gameState.timeLeft)}`, width - 100, 60);
    
    requestAnimationFrame(animate);
  }, [opponentScore, gameState]);
  
  // 事件处理
  const handleMove = useCallback((x, y) => {
    if (!gameState.gameActive) return;
    gameState.trail.push({ x, y });
    if (gameState.trail.length > 8) gameState.trail.shift();
    checkSlice(x, y);
  }, [checkSlice, gameState]);
  
  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    gameState.isDrawing = true;
    gameState.trail = [{ x, y }];
    checkSlice(x, y);
  }, [checkSlice, gameState]);
  
  const drawMove = useCallback((e) => {
    e.preventDefault();
    if (!gameState.isDrawing) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    handleMove(x, y);
  }, [handleMove, gameState]);
  
  const stopDrawing = useCallback((e) => {
    e?.preventDefault();
    gameState.isDrawing = false;
  }, [gameState]);
  
  // 水果生成
  useEffect(() => {
    if (!gameState.gameActive) return;
    const interval = setInterval(() => {
      if (gameState.fruits.length < 20) spawnFruit();
    }, 700);
    return () => clearInterval(interval);
  }, [gameState, spawnFruit]);
  
  // 画布自适应
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const container = canvas.parentElement;
      if (!container) return;
      let maxWidth = Math.min(900, container.clientWidth - 40);
      let height;
      if (isMobile) {
        height = maxWidth * 1.6;
        const maxHeight = window.innerHeight - 150;
        if (height > maxHeight) {
          height = maxHeight;
          maxWidth = height / 1.6;
        }
      } else {
        height = maxWidth * 0.7;
      }
      canvas.style.width = `${maxWidth}px`;
      canvas.style.height = `${height}px`;
      canvas.width = maxWidth;
      canvas.height = height;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [isMobile]);
  
  // 事件绑定
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', drawMove);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', drawMove, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);
    canvas.addEventListener('touchcancel', stopDrawing);
    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', drawMove);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseleave', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', drawMove);
      canvas.removeEventListener('touchend', stopDrawing);
      canvas.removeEventListener('touchcancel', stopDrawing);
    };
  }, [startDrawing, drawMove, stopDrawing]);
  
  // 启动渲染循环
  useEffect(() => {
    gameState.lastTimestamp = 0;
    requestAnimationFrame(animate);
    return () => {};
  }, [animate, gameState]);
  
  return (
    <div style={{ textAlign: 'center', padding: '10px' }}>
      <canvas
        ref={canvasRef}
        style={{
          border: '2px solid #f39c12',
          borderRadius: '16px',
          cursor: 'crosshair',
          touchAction: 'none',
          width: '100%',
          height: 'auto',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
        }}
      />
    </div>
  );
};

export default FruitNinjaGame;